import React, { Component } from 'react';
import {
    Panel,
    PanelType,
    PrimaryButton,
    DefaultButton,
    TextField,
    MessageBar,
    MessageBarType,
    IconButton,
    Dialog,
    DialogType,
    DialogFooter
} from '@fluentui/react';
import { IFeature, IFeaturePermission, splitPrincipals } from './types';
import {
    getFeaturePermissionService,
    addFeaturePermissionService,
    updateFeaturePermissionService
} from './services';
import NoRecordsFound from '../../../../../core/components/NoRecordsFound';
import Template from '../../../../../core/libraries/Template';

interface IProps {
    isOpen: boolean;
    feature: IFeature | null;
    onDismiss: () => void;
}

interface IState {
    permission: IFeaturePermission | undefined;
    originalPermission: IFeaturePermission | undefined; // Track original state for change detection
    newPrincipal: string;
    errorMessage: string;
    successMessage: string;
    isLoading: boolean;
    isNewPermission: boolean; // Track if permission definition doesn't exist yet
    warningMessage: string;
    showClearConfirmDialog: boolean;
}

class FeaturePermissionsPanel extends Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            permission: undefined,
            originalPermission: undefined,
            newPrincipal: '',
            errorMessage: '',
            successMessage: '',
            isLoading: false,
            isNewPermission: false,
            warningMessage: '',
            showClearConfirmDialog: false
        };
    }

    componentDidUpdate(prevProps: IProps) {
        if (this.props.feature !== prevProps.feature || this.props.isOpen !== prevProps.isOpen) {
            if (this.props.isOpen && this.props.feature) {
                this.loadPermissions();
            }
        }
    }

    private loadPermissions = async (): Promise<void> => {
        const { feature } = this.props;
        if (!feature) return;

        this.setState({ isLoading: true, errorMessage: '', successMessage: '', warningMessage: '' });

        try {
            const featurePermission = await getFeaturePermissionService(feature.id!);

            this.setState({
                permission: featurePermission,
                originalPermission: featurePermission, // Store original for comparison
                isLoading: false,
                isNewPermission: false,
                warningMessage: ''
            });
        } catch (error: any) {
            // Check if error is "not found" (404 or similar)
            const isNotFound = error?.message?.toLowerCase().includes('not found') || 
                               error?.status === 404 || 
                               error?.response?.status === 404;
            
            if (isNotFound) {
                // Permission definition hasn't been set yet
                this.setState({
                    permission: {
                        featureId: feature.id,
                        principal: ''
                    },
                    originalPermission: undefined,
                    isLoading: false,
                    isNewPermission: true,
                    warningMessage: 'Feature permission definition has not been set yet. Adding principals will create the initial permission definition.'
                });
            } else {
                this.setState({
                    errorMessage: 'Failed to load permissions',
                    isLoading: false,
                    isNewPermission: false
                });
            }
        }
    };

    private _onAddPrincipal = (): void => {
        const { newPrincipal, permission } = this.state;

        // Validation
        if (!newPrincipal.trim()) {
            this.setState({ errorMessage: 'Principal name is required' });
            return;
        }

        // Get existing principals or empty array
        const existingPrincipals = permission?.principal ? splitPrincipals(permission.principal) : [];
        
        // Split new principals and add them
        const newPrincipals = splitPrincipals(newPrincipal);
        
        // Check for duplicates
        const duplicates = newPrincipals.filter(np => 
            existingPrincipals.some(ep => ep.trim().toLowerCase() === np.trim().toLowerCase())
        );
        
        if (duplicates.length > 0) {
            this.setState({ errorMessage: `Principal(s) already added: ${duplicates.join(', ')}` });
            return;
        }
        
        // Combine existing and new principals
        const combinedPrincipals = [...existingPrincipals, ...newPrincipals];
        const updatedPrincipalString = combinedPrincipals.join(';');
        
        // Update local state
        const updatedPermission = {
            ...permission,
            principal: updatedPrincipalString
        } as IFeaturePermission;
        
        const principalCount = newPrincipals.length;
        const message = principalCount > 1
            ? `${principalCount} principals added (unsaved)`
            : `Principal added: ${newPrincipal} (unsaved)`;
        
        this.setState({
            permission: updatedPermission,
            newPrincipal: '',
            successMessage: message,
            errorMessage: ''
        }, () => {
            setTimeout(() => this.setState({ successMessage: '' }), 5000);
        });
    };

    private _onRemovePrincipal = (principalToRemove: string): void => {
        const { permission } = this.state;

        if (!permission || !permission.principal) return;

        // Remove the specific principal from the permission string
        const currentPrincipals = splitPrincipals(permission.principal);
        const updatedPrincipals = currentPrincipals.filter(p => p.trim() !== principalToRemove.trim());

        // Update local state
        if (updatedPrincipals.length === 0) {
            this.setState({
                permission: {
                    ...permission,
                    principal: ''
                },
                successMessage: `Principal removed: ${principalToRemove} (unsaved)`,
                errorMessage: ''
            }, () => {
                setTimeout(() => this.setState({ successMessage: '' }), 5000);
            });
        } else {
            const updatedPermission = {
                ...permission,
                principal: updatedPrincipals.join(';')
            };

            this.setState({
                permission: updatedPermission,
                successMessage: `Principal removed: ${principalToRemove} (unsaved)`,
                errorMessage: ''
            }, () => {
                setTimeout(() => this.setState({ successMessage: '' }), 5000);
            });
        }
    };

    private _onSave = async (): Promise<void> => {
        const { feature } = this.props;
        const { permission, originalPermission, isNewPermission } = this.state;

        if (!feature) return;

        // For new permissions with no principals, show error since there's nothing to create
        if (isNewPermission && (!permission || !permission.principal?.trim())) {
            this.setState({
                errorMessage: 'Cannot create permission definition without principals. Please add at least one principal.'
            });
            return;
        }

        // For existing permissions, check if principal has changed from the original
        if (!isNewPermission) {
            const hasChanged = permission?.principal !== originalPermission?.principal;
            if (!hasChanged) {
                this.setState({
                    errorMessage: 'No changes detected. Please modify permissions before saving.'
                });
                return;
            }
        }

        // If saving with empty principals on existing permission, show confirmation dialog
        if (!isNewPermission && (!permission || !permission.principal?.trim())) {
            this.setState({ showClearConfirmDialog: true });
            return;
        }

        // Proceed with save
        await this._executeSave();
    };

    private _executeSave = async (): Promise<void> => {
        const { feature } = this.props;
        const { permission, isNewPermission } = this.state;

        if (!feature) return;

        this.setState({ isLoading: true, errorMessage: '', successMessage: '', warningMessage: '', showClearConfirmDialog: false });
        try {
            let savedPermission: IFeaturePermission;
            
            if (isNewPermission) {
                // Create new permission
                savedPermission = await addFeaturePermissionService({
                    featureId: feature.id,
                    principal: permission!.principal
                });
                
                this.setState({
                    permission: savedPermission,
                    originalPermission: savedPermission,
                    successMessage: 'Permission definition created successfully',
                    isLoading: false,
                    isNewPermission: false
                }, () => {
                    setTimeout(() => this.setState({ successMessage: '' }), 5000);
                });
            } else {
                // Update existing permission (allow empty principal - backend will handle removal)
                savedPermission = await updateFeaturePermissionService({
                    id: permission!.id,
                    featureId: feature.id,
                    principal: permission?.principal || ''
                });

                this.setState({
                    permission: savedPermission,
                    originalPermission: savedPermission,
                    successMessage: permission?.principal?.trim() 
                        ? 'Permissions saved successfully' 
                        : 'Permissions cleared successfully',
                    isLoading: false,
                    isNewPermission: false
                }, () => {
                    setTimeout(() => this.setState({ successMessage: '' }), 5000);
                });
            }
        } catch (error) {
            this.setState({
                errorMessage: isNewPermission ? 'Failed to create permission definition' : 'Failed to save permissions',
                isLoading: false
            });
        }
    };

    private _renderPrincipalWithRemove = (principal: string, label?: string): JSX.Element => {
        return (
            <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                marginRight: '8px',
                marginBottom: '8px',
                padding: '4px 8px',
                backgroundColor: '#f3f2f1',
                borderRadius: '4px',
                border: '1px solid #e1dfdd'
            }}>
                <span style={{ fontSize: '13px', color: '#323130' }}>
                    {label || principal}
                </span>
                <IconButton
                    iconProps={{ iconName: 'Cancel' }}
                    title={`Remove ${principal}`}
                    ariaLabel={`Remove ${principal}`}
                    onClick={() => this._onRemovePrincipal(principal)}
                    disabled={this.state.isLoading}
                    styles={{
                        root: { height: '20px', width: '20px' },
                        icon: { fontSize: '10px', color: '#d13438' },
                        rootHovered: { backgroundColor: '#ffebee' }
                    }}
                />
            </div>
        );
    };

    private _renderPrincipals = (): JSX.Element => {
        const { permission } = this.state;

        if (!permission || !permission.principal) {
            return (
                <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#666',
                    border: '1px solid #edebe9',
                    borderRadius: '4px'
                }}>
                    <NoRecordsFound />
                    <div style={{ marginTop: 10, fontSize: '14px' }}>
                        No principals have been granted access yet
                    </div>
                </div>
            );
        }

        // Split the principal string by semicolon
        const principals = splitPrincipals(permission.principal);

        // GUID regex pattern (for groups)
        const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        // User ID pattern (e.g., uib80001, hsy1hc)
        const userIdPattern = /^[a-z]{3}\d[a-z0-9]+$/i;

        // Categorize principals
        const userPrincipals = principals.filter(p => userIdPattern.test(p.trim()));
        const groupPrincipals = principals.filter(p => guidPattern.test(p.trim()));
        const otherPrincipals = principals.filter(p => {
            const trimmed = p.trim();
            return !userIdPattern.test(trimmed) && !guidPattern.test(trimmed);
        });

        return (
            <div style={{
                border: '1px solid #edebe9',
                borderRadius: '4px',
                padding: '20px'
            }}>
                <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '16px', color: '#323130' }}>
                    Granted Access ({principals.length} principal{principals.length !== 1 ? 's' : ''})
                </div>

                {groupPrincipals.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#605e5c', marginBottom: '8px' }}>
                            Groups ({groupPrincipals.length})
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            {groupPrincipals.map((groupId, index) => (
                                <div key={index} style={{ marginRight: '8px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {Template.renderReadOnlyBeautyGroup([groupId])}
                                    <IconButton
                                        iconProps={{ iconName: 'Cancel' }}
                                        title={`Remove group ${groupId}`}
                                        ariaLabel={`Remove group ${groupId}`}
                                        onClick={() => this._onRemovePrincipal(groupId)}
                                        disabled={this.state.isLoading}
                                        styles={{
                                            root: { height: '24px', width: '24px' },
                                            icon: { fontSize: '12px', color: '#d13438' },
                                            rootHovered: { backgroundColor: '#ffebee' }
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {userPrincipals.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#605e5c', marginBottom: '8px' }}>
                            Users ({userPrincipals.length})
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            {userPrincipals.map((userId, index) => (
                                <div key={index} style={{ marginRight: '8px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {Template.renderReadOnlyBeautyPeople([userId])}
                                    <IconButton
                                        iconProps={{ iconName: 'Cancel' }}
                                        title={`Remove user ${userId}`}
                                        ariaLabel={`Remove user ${userId}`}
                                        onClick={() => this._onRemovePrincipal(userId)}
                                        disabled={this.state.isLoading}
                                        styles={{
                                            root: { height: '24px', width: '24px' },
                                            icon: { fontSize: '12px', color: '#d13438' },
                                            rootHovered: { backgroundColor: '#ffebee' }
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {otherPrincipals.length > 0 && (
                    <div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#605e5c', marginBottom: '8px' }}>
                            Other ({otherPrincipals.length})
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            {otherPrincipals.map((principal, index) => (
                                this._renderPrincipalWithRemove(principal)
                            ))}
                        </div>
                    </div>
                )}

                {permission.modifiedAt && (
                    <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #edebe9' }}>
                        <div style={{ fontSize: '12px', color: '#605e5c' }}>
                            Last Modified: {new Date(permission.modifiedAt).toLocaleString()}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    _onPanelDismiss = (): void => {
        this.setState({
            permission: undefined,
            originalPermission: undefined,
        });
        this.props.onDismiss();
    }

    render() {
        const { isOpen, onDismiss, feature } = this.props;
        const {
            newPrincipal,
            errorMessage,
            successMessage,
            isLoading,
            warningMessage
        } = this.state;

        if (!feature) return null;

        return (
            <Panel
                isOpen={isOpen}
                onDismiss={this._onPanelDismiss}
                headerText={`Manage Beta Access: ${feature.title}`}
                closeButtonAriaLabel="Close"
                onRenderFooterContent={() => (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <PrimaryButton 
                            onClick={this._onSave} 
                            disabled={isLoading}
                        >
                            Save
                        </PrimaryButton>
                        <DefaultButton 
                            onClick={this._onPanelDismiss}
                            disabled={isLoading}
                        >
                            Close
                        </DefaultButton>
                    </div>
                )}
                isFooterAtBottom={true}
                customWidth='700px'
                type={PanelType.medium}
                isLightDismiss
            >
                {errorMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={false}
                        onDismiss={() => this.setState({ errorMessage: '' })}
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { marginBottom: 15 } }}
                    >
                        {errorMessage}
                    </MessageBar>
                )}

                {successMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.success}
                        isMultiline={false}
                        onDismiss={() => this.setState({ successMessage: '' })}
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { marginBottom: 15 } }}
                    >
                        {successMessage}
                    </MessageBar>
                )}

                {warningMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.warning}
                        isMultiline={true}
                        onDismiss={() => this.setState({ warningMessage: '' })}
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { marginBottom: 15 } }}
                    >
                        {warningMessage}
                    </MessageBar>
                )}

                <MessageBar
                    messageBarType={MessageBarType.info}
                    isMultiline={true}
                    styles={{ root: { marginBottom: 20 } }}
                >
                    Add users, groups, or roles who should have access to this beta feature. Use user IDs (e.g., uib80001), group IDs (GUIDs), or role names. You can add multiple principals at once by separating them with semicolons (e.g., uib80001;hsy1hc;guid-here).
                </MessageBar>

                <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: 20,
                    alignItems: 'flex-end'
                }}>
                    <TextField
                        label="Add Principal(s)"
                        value={newPrincipal}
                        onChange={(e, newValue) => this.setState({
                            newPrincipal: newValue || '',
                            errorMessage: ''
                        })}
                        placeholder="Enter user ID, group GUID, or role (use ; for multiple)"
                        styles={{ root: { flex: 1 } }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                this._onAddPrincipal();
                            }
                        }}
                        underlined={true}
                    />
                    <DefaultButton
                        iconProps={{ iconName: 'Add' }}
                        text="Add"
                        onClick={this._onAddPrincipal}
                        disabled={isLoading || !newPrincipal.trim()}
                    />
                </div>

                {this._renderPrincipals()}

                {/* Confirmation Dialog for clearing all permissions */}
                <Dialog
                    hidden={!this.state.showClearConfirmDialog}
                    onDismiss={() => this.setState({ showClearConfirmDialog: false })}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: 'Clear All Permissions?',
                        subText: 'You are about to save with no principals. This will completely remove the feature permission definition from the system. Are you sure you want to proceed?'
                    }}
                    modalProps={{
                        isBlocking: true,
                        styles: { main: { maxWidth: 450 } }
                    }}
                >
                    <DialogFooter>
                        <PrimaryButton 
                            onClick={this._executeSave} 
                            text="Confirm"
                            styles={{
                                root: { backgroundColor: '#d13438' },
                                rootHovered: { backgroundColor: '#a4262c' },
                                rootPressed: { backgroundColor: '#8a1c1f' }
                            }}
                        />
                        <DefaultButton 
                            onClick={() => this.setState({ showClearConfirmDialog: false })} 
                            text="Cancel"
                        />
                    </DialogFooter>
                </Dialog>
            </Panel>
        );
    }
}

export default FeaturePermissionsPanel;

